import type { Board } from './types';

export const SIZE = 9;
export const CELLS = 81;

/** Row unit index for each cell. */
export const rowOf: number[] = [];
/** Column unit index for each cell. */
export const colOf: number[] = [];
/** Box unit index for each cell. */
export const boxOf: number[] = [];

for (let i = 0; i < CELLS; i++) {
  const r = Math.floor(i / SIZE);
  const c = i % SIZE;
  rowOf[i] = r;
  colOf[i] = c;
  boxOf[i] = Math.floor(r / 3) * 3 + Math.floor(c / 3);
}

/** The 9 row units, each an array of 9 cell indices. */
export const rows: number[][] = Array.from({ length: SIZE }, () => []);
/** The 9 column units. */
export const cols: number[][] = Array.from({ length: SIZE }, () => []);
/** The 9 box units. */
export const boxes: number[][] = Array.from({ length: SIZE }, () => []);

for (let i = 0; i < CELLS; i++) {
  rows[rowOf[i]!]!.push(i);
  cols[colOf[i]!]!.push(i);
  boxes[boxOf[i]!]!.push(i);
}

/** All 27 units (9 rows, 9 cols, 9 boxes). */
export const units: number[][] = [...rows, ...cols, ...boxes];

/** The three units (row, col, box) each cell belongs to. */
export const unitsOf: number[][][] = Array.from({ length: CELLS }, (_, i) => [
  rows[rowOf[i]!]!,
  cols[colOf[i]!]!,
  boxes[boxOf[i]!]!,
]);

/** Peer sets: all cells sharing a unit with a given cell (excluding itself). */
export const peers: number[][] = Array.from({ length: CELLS }, (_, i) => {
  const set = new Set<number>();
  for (const unit of unitsOf[i]!) {
    for (const j of unit) {
      if (j !== i) set.add(j);
    }
  }
  return [...set];
});

export function boardFromString(s: string): Board {
  const board: Board = [];
  for (const ch of s) {
    if (ch === '0' || ch === '.') board.push(0);
    else if (ch >= '1' && ch <= '9') board.push(Number(ch));
  }
  if (board.length !== CELLS) {
    throw new Error(`Invalid board string: expected ${CELLS} cells, got ${board.length}`);
  }
  return board;
}

export function boardToString(b: Board): string {
  return b.map((v) => (v === 0 ? '0' : String(v))).join('');
}

/** Legal candidates per cell; empty set for filled cells. */
export function candidates(board: Board): Set<number>[] {
  return board.map((value, i) => {
    if (value !== 0) return new Set<number>();
    const used = new Set<number>();
    for (const p of peers[i]!) {
      const v = board[p]!;
      if (v !== 0) used.add(v);
    }
    const set = new Set<number>();
    for (let d = 1; d <= SIZE; d++) {
      if (!used.has(d)) set.add(d);
    }
    return set;
  });
}

/** Peers of `index` that hold the same value as `index` (its conflicts). */
export function conflicts(board: Board, index: number): number[] {
  const value = board[index]!;
  if (value === 0) return [];
  const result: number[] = [];
  for (const p of peers[index]!) {
    if (board[p] === value) result.push(p);
  }
  return result;
}

/** True if no unit contains a duplicated digit (empties ignored). */
export function isValid(board: Board): boolean {
  for (const unit of units) {
    const seen = new Set<number>();
    for (const i of unit) {
      const v = board[i]!;
      if (v === 0) continue;
      if (seen.has(v)) return false;
      seen.add(v);
    }
  }
  return true;
}

/** True if all cells are filled and the board is valid. */
export function isComplete(board: Board): boolean {
  for (let i = 0; i < CELLS; i++) {
    if (board[i] === 0) return false;
  }
  return isValid(board);
}
